import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import {
  IDEMPOTENCY_DISABLED,
  IDEMPOTENCY_KEY,
} from '../../shared-context-tokens/shared-context-tokens';
import {
  FormIdempotencyCoordinatorService,
  ResolvedFormIdempotency,
} from '../../shared-services/idempotency/form-idempotency-coordinator.service';
import { HttpMethods } from '../../constants';

export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

@Injectable()
export class FormIdempotencyKeyInterceptor implements HttpInterceptor {
  private static readonly MUTATING_METHODS = new Set<string>([
    HttpMethods.POST,
    HttpMethods.PUT,
    HttpMethods.PATCH,
    HttpMethods.DELETE,
  ]);

  constructor(
    private readonly coordinator: FormIdempotencyCoordinatorService,
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (this.shouldSkip(request)) {
      return next.handle(request);
    }

    const explicitKey = request.context.get(IDEMPOTENCY_KEY);
    if (explicitKey) {
      return next.handle(
        request.clone({
          setHeaders: { [IDEMPOTENCY_HEADER]: explicitKey },
          context: request.context.set(IDEMPOTENCY_KEY, explicitKey),
        }),
      );
    }

    const resolved = this.coordinator.consumeSubmitIntent();
    if (!resolved) {
      return next.handle(request);
    }

    const cloned = request.clone({
      setHeaders: { [IDEMPOTENCY_HEADER]: resolved.key },
      context: request.context.set(IDEMPOTENCY_KEY, resolved.key),
    });
    return next.handle(cloned).pipe(
      tap((event) => {
        this.rotateKeyAfterSuccess(event, resolved);
      }),
    );
  }

  private shouldSkip(request: HttpRequest<unknown>): boolean {
    return (
      !FormIdempotencyKeyInterceptor.MUTATING_METHODS.has(
        request.method.toUpperCase(),
      ) || request.context.get(IDEMPOTENCY_DISABLED)
    );
  }

  private rotateKeyAfterSuccess(
    event: HttpEvent<unknown>,
    resolved: ResolvedFormIdempotency,
  ): void {
    if (event instanceof HttpResponse && event.ok) {
      this.coordinator.rotateKey(resolved.form);
    }
  }
}
